import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { Logo } from '../components/Logo';
import './Install.css';
import '../pages/admin/Admin.css';
function InstallLayout({ embedded, title, lead, children, }) {
    if (embedded) {
        return (_jsx("div", { className: "admin-page install-page install-embedded", children: _jsxs("div", { className: "install-page-inner", children: [_jsx("header", { className: "admin-page-header", children: _jsxs("div", { children: [_jsx("h1", { children: title }), _jsx("p", { children: lead })] }) }), children] }) }));
    }
    return (_jsx("div", { className: "install-page", children: _jsxs("div", { className: "install-page-inner", children: [_jsx("div", { className: "install-page-brand", children: _jsx(Logo, { className: "huge" }) }), _jsx("h1", { className: "install-page-title", children: title }), _jsx("p", { className: "install-page-lead", children: lead }), children] }) }));
}
async function readJsonResponse(res) {
    const contentType = res.headers.get('content-type') ?? '';
    const responseText = await res.text();
    if (!contentType.toLowerCase().includes('application/json')) {
        const preview = responseText.slice(0, 200).replace(/\s+/g, ' ').trim();
        throw new Error(`Expected JSON but got ${contentType || 'unknown'} (HTTP ${res.status}). Body: ${preview}`);
    }
    if (!res.ok) {
        const preview = responseText.slice(0, 200).replace(/\s+/g, ' ').trim();
        throw new Error(`API error (HTTP ${res.status}): ${preview}`);
    }
    return JSON.parse(responseText);
}
export function Install({ embedded = false }) {
    const [installPassword, setInstallPassword] = useState(() => sessionStorage.getItem('installPassword') ?? '');
    const [unlocked, setUnlocked] = useState(false);
    const [status, setStatus] = useState(null);
    const [migrationsData, setMigrationsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [migrationsLoading, setMigrationsLoading] = useState(false);
    const [installing, setInstalling] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [applyingMigrations, setApplyingMigrations] = useState(false);
    const [log, setLog] = useState([]);
    const getInstallHeaders = useCallback(() => {
        return {
            'X-Install-Password': installPassword,
        };
    }, [installPassword]);
    const loadInstallPageData = useCallback(async (silent) => {
        if (!unlocked)
            return;
        setLoading(true);
        setMigrationsLoading(true);
        try {
            const [statusRes, migRes] = await Promise.all([
                fetch('/api/install/status', { headers: getInstallHeaders() }),
                fetch('/api/install/migrations', { headers: getInstallHeaders() }),
            ]);
            const statusData = await readJsonResponse(statusRes);
            const migData = await readJsonResponse(migRes);
            setStatus(statusData);
            setMigrationsData(migData);
            if (!silent) {
                setLog((prev) => [...prev, `[${new Date().toISOString()}] ${statusData.message}`]);
            }
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to reach API';
            setStatus({ ready: false, needsInstall: true, message: msg });
            setMigrationsData(null);
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
        }
        finally {
            setLoading(false);
            setMigrationsLoading(false);
        }
    }, [getInstallHeaders, unlocked]);
    useEffect(() => {
        if (!unlocked)
            return;
        loadInstallPageData(false);
    }, [unlocked, loadInstallPageData]);
    const handleUnlock = async () => {
        if (!installPassword.trim()) {
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: Install password is required.`]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch('/api/install/unlock', {
                method: 'POST',
                headers: getInstallHeaders(),
            });
            await readJsonResponse(res);
            sessionStorage.setItem('installPassword', installPassword);
            setUnlocked(true);
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Install access granted.`]);
        }
        catch (err) {
            sessionStorage.removeItem('installPassword');
            setUnlocked(false);
            const msg = err instanceof Error ? err.message : 'Invalid install password';
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleInstall = async () => {
        setInstalling(true);
        setLog((prev) => [...prev, `[${new Date().toISOString()}] Starting installation...`]);
        try {
            const res = await fetch('/api/install', { method: 'POST', headers: getInstallHeaders() });
            const data = await readJsonResponse(res);
            for (const r of data.results) {
                const line = r.success
                    ? `  ✓ ${r.scriptName}`
                    : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
                setLog((prev) => [...prev, line]);
            }
            setLog((prev) => [
                ...prev,
                `[${new Date().toISOString()}] ${data.success ? 'Installation completed successfully.' : 'Installation completed with errors.'}`,
            ]);
            if (data.success) {
                await loadInstallPageData(true);
            }
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Install request failed';
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
        }
        finally {
            setInstalling(false);
        }
    };
    const handleSeed = async () => {
        setSeeding(true);
        setLog((prev) => [...prev, `[${new Date().toISOString()}] Starting seed...`]);
        try {
            const res = await fetch('/api/install/seed', { method: 'POST', headers: getInstallHeaders() });
            const data = await readJsonResponse(res);
            for (const r of data.results) {
                const line = r.success
                    ? `  ✓ ${r.scriptName}`
                    : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
                setLog((prev) => [...prev, line]);
            }
            setLog((prev) => [
                ...prev,
                `[${new Date().toISOString()}] ${data.success ? 'Seed completed successfully.' : 'Seed completed with errors.'}`,
            ]);
            await loadInstallPageData(true);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Seed request failed';
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
        }
        finally {
            setSeeding(false);
        }
    };
    const handleApplyMigrations = async () => {
        setApplyingMigrations(true);
        setLog((prev) => [...prev, `[${new Date().toISOString()}] Bekleyen SQL migration'ları uygulanıyor...`]);
        try {
            const res = await fetch('/api/install/migrations/apply', {
                method: 'POST',
                headers: getInstallHeaders(),
            });
            const data = await readJsonResponse(res);
            if (data.results.length === 0) {
                setLog((prev) => [...prev, `  (Bekleyen migration yok)`]);
            }
            for (const r of data.results) {
                const line = r.success
                    ? `  ✓ ${r.scriptName}`
                    : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
                setLog((prev) => [...prev, line]);
            }
            setLog((prev) => [
                ...prev,
                `[${new Date().toISOString()}] ${data.success ? "Migration'lar tamamlandı." : "Migration'lar hata ile bitti."}`,
            ]);
            await loadInstallPageData(true);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Migration isteği başarısız';
            setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
        }
        finally {
            setApplyingMigrations(false);
        }
    };
    const migrationSectionBusy = installing || seeding || applyingMigrations;
    const pendingMigrations = migrationsData?.databaseReachable === true
        ? migrationsData.migrations.filter((m) => m.applied === false).length
        : null;
    const renderMigrationsPanel = () => (_jsxs("div", { className: "install-panel install-migrations", children: [_jsxs("div", { className: "install-migrations-header", children: [_jsx("span", { className: "install-migrations-title", children: "SQL migration dosyalar\u0131" }), migrationsLoading ? (_jsx("span", { className: "install-migrations-meta", children: "Y\u00FCkleniyor\u2026" })) : migrationsData ? (_jsx("span", { className: "install-migrations-meta", children: migrationsData.databaseReachable ? (_jsxs(_Fragment, { children: ["Bekleyen: ", _jsx("strong", { children: pendingMigrations })] })) : (_jsxs("span", { className: "install-migrations-warn", children: ["Veritaban\u0131na ula\u015F\u0131lamad\u0131", migrationsData.message ? ` — ${migrationsData.message}` : ''] })) })) : null] }), migrationsData && migrationsData.migrations.length > 0 ? (_jsx("div", { className: "install-migrations-table-wrap", children: _jsxs("table", { className: "install-migrations-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Dosya" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "Uygulanma" })] }) }), _jsx("tbody", { children: migrationsData.migrations.map((m) => (_jsxs("tr", { children: [_jsx("td", { className: "install-migrations-name", children: m.name }), _jsxs("td", { children: [m.applied === true && _jsx("span", { className: "install-migrations-badge applied", children: "Uyguland\u0131" }), m.applied === false && _jsx("span", { className: "install-migrations-badge pending", children: "Bekliyor" }), m.applied === null && _jsx("span", { className: "install-migrations-badge unknown", children: "Bilinmiyor" })] }), _jsx("td", { className: "install-migrations-date", children: m.appliedAt ? new Date(m.appliedAt).toLocaleString() : '—' })] }, m.name))) })] }) })) : (!migrationsLoading && (_jsx("p", { className: "install-migrations-empty", children: "Tan\u0131ml\u0131 db.migration-*.sql dosyas\u0131 yok." }))), _jsx("div", { className: "install-migrations-actions", children: _jsx("button", { type: "button", className: "install-btn install-btn-primary", onClick: handleApplyMigrations, disabled: migrationSectionBusy ||
                        migrationsLoading ||
                        !migrationsData?.databaseReachable ||
                        pendingMigrations === 0, children: applyingMigrations ? 'Uygulanıyor…' : "Bekleyen migration'ları çalıştır" }) })] }));
    const renderLogPanel = (emptyLabel = 'Waiting...') => (_jsx("div", { className: "install-panel install-log-panel", children: _jsxs("div", { className: "install-log-container", children: [_jsx("div", { className: "install-log-header", children: "Execution Log" }), _jsx("pre", { className: "install-log", children: log.length === 0 ? emptyLabel : log.join('\n') })] }) }));
    const renderMigrationsAndLog = (emptyLogLabel = 'Waiting...') => (_jsxs("section", { className: "install-section install-split", children: [renderMigrationsPanel(), renderLogPanel(emptyLogLabel)] }));
    if (!unlocked) {
        return (_jsxs(InstallLayout, { embedded: embedded, title: "Install Access", lead: "Kuruluma devam etmek i\u00E7in install \u015Fifresini girin.", children: [_jsxs("section", { className: "install-section", children: [_jsxs("div", { className: "install-field", children: [_jsx("label", { className: "install-label", htmlFor: "install-password", children: "Install password" }), _jsx("input", { id: "install-password", type: "password", value: installPassword, onChange: (e) => setInstallPassword(e.target.value), className: "install-input", placeholder: "Install password", autoComplete: "off", onKeyDown: (e) => {
                                        if (e.key === 'Enter')
                                            void handleUnlock();
                                    } })] }), _jsx("div", { className: "install-actions", children: _jsx("button", { type: "button", className: "install-btn install-btn-primary", onClick: handleUnlock, disabled: loading, children: loading ? 'Checking...' : 'Unlock' }) })] }), log.length > 0 && (_jsx("section", { className: "install-section", children: renderLogPanel() }))] }));
    }
    if (status?.ready) {
        return (_jsxs(InstallLayout, { embedded: embedded, title: "Database Ready", lead: status.message, children: [_jsxs("section", { className: "install-section", children: [_jsx("h2", { className: "install-section-title", children: "Seed" }), _jsx("p", { className: "install-section-text", children: "Seed script\u2019ini yeniden \u00E7al\u0131\u015Ft\u0131rabilirsiniz." }), _jsx("div", { className: "install-actions", children: _jsx("button", { type: "button", className: "install-btn install-btn-primary", onClick: handleSeed, disabled: seeding || applyingMigrations, children: seeding ? 'Seeding...' : 'Run Seed' }) })] }), renderMigrationsAndLog()] }));
    }
    return (_jsxs(InstallLayout, { embedded: embedded, title: "Database Setup", lead: loading ? 'Checking database...' : status?.message ?? 'Unknown status', children: [status?.needsInstall && !loading && (_jsxs("section", { className: "install-section", children: [_jsx("h2", { className: "install-section-title", children: "Kurulum" }), _jsx("p", { className: "install-section-text", children: "\u015Eema kurulumu ve seed i\u015Flemlerini buradan \u00E7al\u0131\u015Ft\u0131r\u0131n." }), _jsxs("div", { className: "install-actions", children: [_jsx("button", { type: "button", className: "install-btn install-btn-primary", onClick: handleInstall, disabled: migrationSectionBusy, children: installing ? 'Installing...' : 'Install Database' }), _jsx("button", { type: "button", className: "install-btn", onClick: handleSeed, disabled: migrationSectionBusy, children: seeding ? 'Seeding...' : 'Run Seed' })] })] })), renderMigrationsAndLog()] }));
}
